import { createContext, useCallback, useEffect, useState, ReactNode } from 'react'
import type { KeycloakInitOptions } from 'keycloak-js'
import { keycloak, initKeycloakSafe, keycloakInitOptions, keycloakIdpHint } from './keycloak'

export interface AuthUser {
  id: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  tenantId: string
  roles: string[]
  portalAccess: string[]
  tenantType?: string
  accountType?: string
}

export interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  accessToken: string | null
  portalAccess: string[]
  hasPortalAccess: (portal: 'client' | 'adminclient' | 'admin') => boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
}

export const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

// Paths that must remain reachable WITHOUT a Keycloak session. Everything else
// forces the Keycloak hosted login (onLoad: 'login-required') — there is no
// in-app login/landing page anymore.
const PUBLIC_PATH_PREFIXES = ['/signup', '/trust/', '/page/', '/versionsbuilt']
function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p))
}

function extractPortalAccess(tp: any): string[] {
  const raw = tp?.portal_access
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') return raw.split(',').map((s: string) => s.trim())
  return ['client'] // Default: client portal access
}

// Decode the JWT payload without verifying the signature. Used to inspect
// `exp` so we can avoid forcing a refresh-token replay on every page load.
function decodeJwtPayload(tok: string): any | null {
  try {
    return JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

function isJwtExpired(tok: string): boolean {
  const payload = decodeJwtPayload(tok)
  if (!payload || typeof payload.exp !== 'number') return true
  // 30s safety margin so we don't hand kc a token that expires mid-request
  return Date.now() >= payload.exp * 1000 - 30_000
}

function extractUser(): AuthUser | null {
  const tp = keycloak.tokenParsed
  if (!tp) return null
  return {
    id: tp.sub || '',
    email: tp.email || '',
    name: tp.name || tp.preferred_username || '',
    firstName: tp.given_name,
    lastName: tp.family_name,
    tenantId: (tp as any).tenant_id || 'default-tenant',
    roles: tp.realm_access?.roles || [],
    portalAccess: extractPortalAccess(tp),
    tenantType: (tp as any).tenant_type,
    accountType: (tp as any).account_type,
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)

  // E2E bypass mode — uses real database user/tenant from env vars
  const isE2EBypass = import.meta.env.VITE_E2E_BYPASS_AUTH === 'true'

  useEffect(() => {
    if (isE2EBypass) {
      setIsAuthenticated(true)
      setAccessToken(import.meta.env.VITE_E2E_TOKEN || 'e2e-bypass')
      setUser({
        id: import.meta.env.VITE_E2E_USER_ID || '20000000-0000-0000-0000-000000000010',
        email: import.meta.env.VITE_E2E_EMAIL || 'stevenoubissie@yahpo.fr',
        name: import.meta.env.VITE_E2E_NAME || 'Steve Noubissie',
        firstName: import.meta.env.VITE_E2E_FIRST_NAME || 'Steve',
        lastName: import.meta.env.VITE_E2E_LAST_NAME || 'Noubissie',
        tenantId: import.meta.env.VITE_E2E_TENANT_ID || '550e8400-e29b-41d4-a716-446655440001',
        roles: (import.meta.env.VITE_E2E_ROLES || 'ADMIN').split(','),
        portalAccess: (import.meta.env.VITE_E2E_PORTAL_ACCESS || 'client').split(','),
        tenantType: import.meta.env.VITE_E2E_TENANT_TYPE || 'default',
        accountType: import.meta.env.VITE_E2E_ACCOUNT_TYPE || 'team',
      })
      setIsLoading(false)
      return
    }

    // Register event handlers BEFORE init so they're ready for the callback
    keycloak.onTokenExpired = async () => {
      try {
        const refreshed = await keycloak.updateToken(30)
        if (refreshed) {
          setAccessToken(keycloak.token || null)
        }
      } catch {
        setIsAuthenticated(false)
        setUser(null)
        setAccessToken(null)
      }
    }

    // (Old onAuthSuccess handler removed — replaced by token-saving version above)

    keycloak.onAuthError = (errorData) => {
      console.error('[AuthProvider] onAuthError fired:', errorData)
      setIsAuthenticated(false)
      setUser(null)
      setAccessToken(null)
    }

    keycloak.onAuthLogout = () => {
      try { localStorage.removeItem('kc.token'); localStorage.removeItem('kc.refreshToken') } catch {}
      setIsAuthenticated(false)
      setUser(null)
      setAccessToken(null)
    }

    // Restore tokens from localStorage if present so hard page reloads keep auth.
    // keycloak-js stores tokens in memory only — without this, every page.goto
    // (Playwright or user direct nav) loses the session.
    const savedToken = (() => {
      try { return localStorage.getItem('kc.token') } catch { return null }
    })()
    const savedRefresh = (() => {
      try { return localStorage.getItem('kc.refreshToken') } catch { return null }
    })()
    // Refresh-token replay race fix:
    // keycloak.init({token, refreshToken}) FORCES an immediate token refresh
    // against the IdP. With realm-level refresh-token rotation enabled, each
    // refresh_token is single-use — so two near-simultaneous page loads (or
    // Playwright's page.goto storm) both replay the SAME saved refresh_token
    // and the second call returns invalid_grant 400, dropping us to unauth.
    //
    // Strategy:
    //  1. If the saved access token is still valid, hand kc only its config
    //     (no token/refreshToken) so init does NOT call /token, then set the
    //     tokens directly on the instance. This is the hot path on reload.
    //  2. If the access token is expired but we still have a refresh token,
    //     pass refreshToken only — kc will use it once, get a new pair, and
    //     onAuthRefreshSuccess persists the rotated pair.
    //  3. Otherwise fall back to onLoad: check-sso (or default).
    const hasCode = window.location.search.includes('code=') || window.location.hash.includes('code=')

    let useDirectAttach = false
    let initOpts: KeycloakInitOptions
    if (savedToken && savedRefresh && !isJwtExpired(savedToken)) {
      // Hot path: token still valid — skip the forced refresh that causes
      // refresh_token replay. We attach the tokens AFTER init resolves.
      useDirectAttach = true
      initOpts = { ...keycloakInitOptions }
    } else if (savedRefresh) {
      // Saved access token is gone or expired — let kc consume the refresh
      // token exactly once. The next reload will land in the hot path again.
      initOpts = { ...keycloakInitOptions, refreshToken: savedRefresh }
    } else if (hasCode) {
      // Returning from the Keycloak hosted login with an auth code — exchange
      // it silently (check-sso avoids the 3rd-party-cookie iframe check).
      initOpts = { ...keycloakInitOptions, onLoad: 'check-sso' as const }
    } else if (isPublicPath(window.location.pathname)) {
      // Public pages (signup, public trust center) stay reachable unauthenticated.
      initOpts = keycloakInitOptions
    } else {
      // No session and not a public page → send the user STRAIGHT to the
      // Keycloak hosted login. There is no in-app login page to fall back to.
      initOpts = { ...keycloakInitOptions, onLoad: 'login-required' as const }
    }

    // Persist tokens whenever they change so the next page load can resume.
    keycloak.onAuthSuccess = () => {
      try {
        if (keycloak.token) localStorage.setItem('kc.token', keycloak.token)
        if (keycloak.refreshToken) localStorage.setItem('kc.refreshToken', keycloak.refreshToken)
      } catch {}
      console.log('[AuthProvider] onAuthSuccess — token exchange succeeded; saved to localStorage')
    }
    keycloak.onAuthRefreshSuccess = () => {
      try {
        if (keycloak.token) localStorage.setItem('kc.token', keycloak.token)
        if (keycloak.refreshToken) localStorage.setItem('kc.refreshToken', keycloak.refreshToken)
      } catch {}
    }

    let resolved = false
    initKeycloakSafe(initOpts).then((authenticated) => {
      resolved = true
      console.log('[AuthProvider] initKeycloakSafe result:', authenticated, 'directAttach:', useDirectAttach)

      // Hot-path direct attach: kc.init() was called with no token, so
      // `authenticated` is false. Hydrate the instance from localStorage
      // and treat the session as authenticated until the access token
      // actually expires (onTokenExpired then drives a single refresh).
      if (useDirectAttach && savedToken && savedRefresh) {
        keycloak.token = savedToken
        keycloak.refreshToken = savedRefresh
        const parsed = decodeJwtPayload(savedToken)
        if (parsed) {
          ;(keycloak as any).tokenParsed = parsed
        }
        ;(keycloak as any).authenticated = true

        // E2E skip-refresh branch:
        // When tokens come from a programmatic password-grant injection (see
        // e2e-tests/helpers/keycloak-storage-state.ts), the realm's session
        // table has no matching session_state for the refresh_token. The
        // first keycloak-js silent-refresh attempt would POST that token to
        // /protocol/openid-connect/token, get 400, fire onAuthError, and
        // cascade 401s onto every API call. Clamp timeSkew so kc believes
        // the token is fresh for the next hour, and stamp tokenParsed.exp
        // to match so isJwtExpired() / our own callers also see "fresh".
        // Disable the 3rd-party login iframe check too — it triggers the
        // same refresh path on an interval.
        if ((window as any).__E2E_SKIP_REFRESH__) {
          try {
            const farFuture = Math.floor(Date.now() / 1000) + 3600
            ;(keycloak as any).tokenParsed = {
              ...(keycloak.tokenParsed || {}),
              exp: farFuture,
            }
            // Negative timeSkew makes kc think we're an hour behind the IdP,
            // so updateToken(minValidity) sees plenty of headroom and short
            // -circuits without a network call.
            ;(keycloak as any).timeSkew = -3600
            // Belt-and-suspenders: neuter onTokenExpired so even if kc's
            // internal expiration timer fires anyway, we don't replay the
            // doomed refresh_token.
            keycloak.onTokenExpired = () => {
              console.log('[AuthProvider] onTokenExpired suppressed (E2E skip-refresh)')
            }
            console.log('[AuthProvider] E2E skip-refresh engaged — timeSkew=-3600, exp=+1h')
          } catch (e) {
            console.warn('[AuthProvider] failed to engage E2E skip-refresh', e)
          }
        }

        setIsAuthenticated(true)
        setAccessToken(savedToken)
        setUser(extractUser())
        setIsLoading(false)
        return
      }

      setIsAuthenticated(authenticated)
      if (authenticated) {
        setAccessToken(keycloak.token || null)
        setUser(extractUser())
      }
      setIsLoading(false)
    })
    // Hard fallback: never let isLoading remain true past 15s even if KC init
    // hangs. But skip the fallback if the URL contains a KC callback code/state
    // (we're mid-token-exchange and shouldn't bail early).
    const hasCallbackParams = (() => {
      try {
        const u = new URL(window.location.href)
        return u.searchParams.has('code') || u.searchParams.has('state') || window.location.hash.includes('code=')
      } catch { return false }
    })()
    const fallbackMs = hasCallbackParams ? 25000 : 15000
    const fallback = setTimeout(() => {
      if (!resolved) {
        console.warn(`[AuthProvider] KC init hung past ${fallbackMs}ms; unblocking with unauthenticated state`)
        setIsAuthenticated(false)
        setIsLoading(false)
      }
    }, fallbackMs)
    return () => clearTimeout(fallback)
  }, [isE2EBypass])

  const login = useCallback(async () => {
    try {
      // No in-app /login page — bounce back to the app root after auth.
      const redirectUri = window.location.origin + '/'
      console.log('[AuthProvider] login() called:', {
        authenticated: keycloak.authenticated,
        redirectUri,
        idpHint: keycloakIdpHint,
        origin: window.location.origin,
      })
      await keycloak.login({
        idpHint: keycloakIdpHint,
        redirectUri,
      })
    } catch (error) {
      console.error('[AuthProvider] login failed:', error)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      // Purge persisted tokens BEFORE the redirect so the return trip to '/'
      // starts with no session and cleanly hits login-required (not a stale
      // direct-attach).
      try { localStorage.removeItem('kc.token'); localStorage.removeItem('kc.refreshToken') } catch {}
      await keycloak.logout({
        // Land on the app root; when unauthenticated it immediately bounces to
        // the Keycloak hosted login (login-required), never an in-app page.
        redirectUri: window.location.origin + '/',
      })
    } catch (error) {
      console.error('[AuthProvider] logout failed:', error)
    }
  }, [])

  const refreshTokenFn = useCallback(async () => {
    try {
      const refreshed = await keycloak.updateToken(30)
      if (refreshed) {
        setAccessToken(keycloak.token || null)
      }
      return refreshed
    } catch (error) {
      console.error('[AuthProvider] token refresh failed:', error)
      return false
    }
  }, [])

  const portalAccess = user?.portalAccess || []
  const hasPortalAccess = (portal: 'client' | 'adminclient' | 'admin') => portalAccess.includes(portal)

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    accessToken,
    portalAccess,
    hasPortalAccess,
    login,
    logout,
    refreshToken: refreshTokenFn,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
